import { useCallback, useEffect, useRef, useState } from 'react';
import { subscribeToasts } from '../utils/toast';
import './Toaster.css';

const MAX_SHOWN = 5;

const ICONS = {
  success: <path d="M5 12.5l4.2 4.2L19 7" />,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 7.5h.01" /></>,
  warn: <><path d="M10.3 3.9L2.6 17.5A2 2 0 0 0 4.3 20.5h15.4a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /><path d="M12 9.5v4M12 17h.01" /></>,
  error: <><circle cx="12" cy="12" r="9" /><path d="M15 9l-6 6M9 9l6 6" /></>,
};

function ToastItem({ item, onClose }) {
  const [leaving, setLeaving] = useState(false);
  const [paused, setPaused] = useState(false);
  const remaining = useRef(item.duration);
  const started = useRef(Date.now());

  const close = useCallback(() => {
    setLeaving(true);
    setTimeout(() => onClose(item.id), 180);
  }, [item.id, onClose]);

  useEffect(() => {
    if (!item.duration || paused) return undefined;
    started.current = Date.now();
    const timer = setTimeout(close, remaining.current);
    return () => {
      clearTimeout(timer);
      remaining.current -= Date.now() - started.current;
    };
  }, [item.duration, paused, close]);

  return (
    <div
      className={`toast toast--${item.type}${leaving ? ' is-leaving' : ''}`}
      role={item.type === 'error' ? 'alert' : 'status'}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <span className="toast__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          {ICONS[item.type]}
        </svg>
      </span>
      <div className="toast__body">
        {item.title ? <strong>{item.title}</strong> : null}
        {item.message ? <p>{item.message}</p> : null}
        {item.action ? (
          <button
            type="button"
            className="toast__action"
            onClick={() => {
              item.action.onClick?.();
              close();
            }}
          >
            {item.action.label}
          </button>
        ) : null}
      </div>
      <button type="button" className="toast__close" onClick={close} aria-label="Dismiss">
        ×
      </button>
      {item.duration ? (
        <span
          className="toast__bar"
          style={{ animationDuration: `${item.duration}ms`, animationPlayState: paused ? 'paused' : 'running' }}
        />
      ) : null}
    </div>
  );
}

/** Stack of toast messages: bottom centre (top, full width, on phones). */
function Toaster() {
  const [items, setItems] = useState([]);

  useEffect(
    () =>
      subscribeToasts((event) => {
        if (event.kind === 'add') setItems((prev) => [...prev, event.item].slice(-MAX_SHOWN));
        if (event.kind === 'remove') setItems((prev) => prev.filter((t) => t.id !== event.id));
      }),
    [],
  );

  const remove = useCallback((id) => setItems((prev) => prev.filter((t) => t.id !== id)), []);

  return (
    <div className="toaster" aria-live="polite">
      {items.map((item) => (
        <ToastItem key={item.id} item={item} onClose={remove} />
      ))}
    </div>
  );
}

export default Toaster;
